<template>
    <Head title="EDS Operations Dashboard" />
    <app-layout>
        <div class="smiths-dashboard">
            <div class="smiths-dashboard__header">
                <div class="flex flex-wrap items-center gap-3">
                    <div class="flex items-center gap-2">
                        <label class="text-sm font-medium text-muted" for="site-select">Site</label>
                        <select id="site-select" v-model="selectedSite" class="form-select form-select-sm" style="min-width: 100px">
                            <option value="">All sites</option>
                            <option v-for="site in siteIds" :key="site" :value="site">{{ site }}</option>
                        </select>
                    </div>
                    <div class="flex items-center gap-2">
                        <label class="text-sm font-medium text-muted" for="unit-select">Unit</label>
                        <select id="unit-select" v-model="selectedUnit" class="form-select form-select-sm" style="min-width: 120px">
                            <option value="">Select unit...</option>
                            <option v-for="unit in filteredUnits" :key="unit" :value="unit">{{ unit }}</option>
                        </select>
                    </div>
                    <div class="flex items-center gap-0">
                        <label class="me-2 text-sm font-medium text-muted" for="month-select">Month</label>
                        <button class="month-nav-btn text-sm font-medium text-muted" type="button" @click="shiftMonth(-1)">-</button>
                        <input id="month-select" v-model="selectedMonth" class="form-control form-control-sm" style="width: 150px" type="month" />
                        <button class="month-nav-btn text-sm font-medium text-muted" type="button" @click="shiftMonth(1)">+</button>
                    </div>
                    <CButton v-if="pendingReviewCount > 0" color="warning" size="sm" @click="showReviewModal = true">
                        <icon icon="cil-warning" class="me-1" />
                        {{ pendingReviewCount }} Pending Review{{ pendingReviewCount !== 1 ? 's' : '' }}
                    </CButton>
                    <jet-button v-if="isDebug" class="btn-icon" variant="secondary" @click="showDebugPanel = !showDebugPanel">
                        <icon name="cil-bug" />
                    </jet-button>
                    <CButton color="primary" size="sm" @click="showImportModal = true">
                        <icon icon="cil-cloud-upload" class="me-1" />
                        Import
                    </CButton>
                </div>
            </div>

            <div v-if="$page.props.flash?.success" class="mt-3">
                <CAlert color="success" dismissible>{{ $page.props.flash.success }}</CAlert>
            </div>

            <template v-if="selectedUnit && selectedMonth">
                <div class="mt-4 flex flex-col gap-4">
                    <CAlert v-if="chartData && !hasScreeningData" color="info">
                        No bag scan data for this period — showing system events only.
                    </CAlert>

                    <L1DecisionBreakdownChart v-if="hasScreeningData" :data="chartData?.l1DecisionBreakdown" :loading="loading" />
                    <AvailabilityChart :data="chartData?.availability" :loading="loading" />
                    <DowntimeBreakdownChart :data="chartData?.downtimeBreakdown" :loading="loading" />
                    <BagJamsChart
                        v-if="hasScreeningData"
                        :data="chartData?.bagJamsVsThroughput"
                        :downtime-data="chartData?.downtimeBreakdown"
                        :loading="loading"
                    />
                    <SystemEventsChart :data="chartData?.systemEvents" :loading="loading" />
                    <SummaryKpiRow v-if="hasScreeningData" :data="chartData?.summaryKpis" :loading="loading" />
                    <template v-if="hasScreeningData">
                        <ThresholdAreaChart
                            :data="chartData?.mistrack"
                            :loading="loading"
                            dataset-label="Mistrack %"
                            kpi-label="Overall Mistrack"
                            title="Overall Mistrack"
                        />
                        <ThresholdAreaChart
                            :data="chartData?.l1Reject"
                            :loading="loading"
                            dataset-label="L1 Reject %"
                            kpi-label="Level 1 Reject"
                            title="Level 1 Reject"
                        />
                        <ThresholdAreaChart
                            :data="chartData?.bna"
                            :loading="loading"
                            dataset-label="BNA %"
                            kpi-label="BNA"
                            title="BNA (Bags Not Analysed)"
                            empty-text="No BNA data — the import didn't include a BNA feed."
                        />
                    </template>

                    <CModal v-if="isDebug" :visible="showDebugPanel" fullscreen @close="showDebugPanel = false">
                        <CModalHeader>
                            <CModalTitle>Daily Data Sheet — Debug</CModalTitle>
                        </CModalHeader>
                        <CModalBody>
                            <div v-if="chartData?.debug" class="table-responsive">
                                <table class="table-sm table-bordered table-striped font-monospace small table">
                                    <thead class="table-light">
                                        <tr>
                                            <th>Date</th>
                                            <th>Avail %</th>
                                            <th>Downtime</th>
                                            <th>MDT</th>
                                            <th>Bag Jams</th>
                                            <th>BJ Time</th>
                                            <th>Flt Rst</th>
                                            <th>Shutdowns</th>
                                            <th>Startups</th>
                                            <th>Sys Err</th>
                                            <th>Throughput</th>
                                            <th>L1 Clear</th>
                                            <th>L1 Reject</th>
                                            <th>BNA</th>
                                            <th>Op Clear</th>
                                            <th>Op Reject</th>
                                            <th>L1 Clear %</th>
                                            <th>L1 Reject %</th>
                                            <th>BNA %</th>
                                            <th>Mistrack</th>
                                            <th>Phantom</th>
                                            <th>Review</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in chartData.debug" :key="row.date" :class="{ 'table-warning': row.needsReview > 0 }">
                                            <td class="text-nowrap">{{ row.date }}</td>
                                            <td>{{ row.availabilityPct != null ? row.availabilityPct.toFixed(2) + '%' : '' }}</td>
                                            <td>{{ fmtDur(row.downtimeMinutes) }}</td>
                                            <td>{{ fmtDur(row.mdtMinutes) }}</td>
                                            <td>{{ row.bagJams }}</td>
                                            <td>{{ fmtDur(row.bagJamMinutes) }}</td>
                                            <td>{{ row.faultResets }}</td>
                                            <td>{{ row.shutdowns }}</td>
                                            <td>{{ row.startups }}</td>
                                            <td>{{ row.systemErrors }}</td>
                                            <td>{{ row.throughput }}</td>
                                            <td>{{ row.l1Clear }}</td>
                                            <td>{{ row.l1Reject }}</td>
                                            <td>{{ row.bna }}</td>
                                            <td>{{ row.operatorClear }}</td>
                                            <td>{{ row.operatorReject }}</td>
                                            <td>{{ row.l1ClearPct.toFixed(4) }}</td>
                                            <td>{{ row.l1RejectPct.toFixed(4) }}</td>
                                            <td>{{ row.bnaPct.toFixed(4) }}</td>
                                            <td>{{ row.mistrack }}</td>
                                            <td>{{ row.phantom }}</td>
                                            <td>{{ row.needsReview > 0 ? '⚠ ' + row.needsReview : '' }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p v-else class="text-muted">No data loaded.</p>
                        </CModalBody>
                    </CModal>
                </div>
            </template>
            <template v-else>
                <div class="mt-12 flex flex-col items-center justify-center gap-4 text-center text-muted">
                    <icon icon="cil-chart-line" size="3xl" />
                    <p>Select a unit and month to view EDS operations data, or import new data.</p>
                </div>
            </template>
        </div>

        <ImportModal :visible="showImportModal" @close="showImportModal = false" />
        <ReviewModal
            :month="selectedMonth"
            :unit-identifier="selectedUnit"
            :visible="showReviewModal"
            @close="showReviewModal = false"
            @updated="fetchChartData"
        />
    </app-layout>
</template>

<script setup>
import { Head } from '@inertiajs/vue3';
import { ref, computed, onMounted, watch } from 'vue';
import { CButton, CAlert, CModal, CModalHeader, CModalTitle, CModalBody } from '@coreui/vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import ImportModal from './Components/ImportModal.vue';
import L1DecisionBreakdownChart from './Components/L1DecisionBreakdownChart.vue';
import SummaryKpiRow from './Components/SummaryKpiRow.vue';
import BagJamsChart from './Components/BagJamsChart.vue';
import SystemEventsChart from './Components/SystemEventsChart.vue';
import JetButton from '@/Jetstream/Button.vue';
import AvailabilityChart from './Components/AvailabilityChart.vue';
import DowntimeBreakdownChart from './Components/DowntimeBreakdownChart.vue';
import ReviewModal from './Components/ReviewModal.vue';
import ThresholdAreaChart from './Components/ThresholdAreaChart.vue';
import { $http } from '@/bootstrap.js';
import { buildUrlWithQuery } from '@/Utils/buildUrlWithQuery';

const props = defineProps({
    siteIds: {
        type: Array,
        default: () => [],
    },
    unitsBySite: {
        type: Object,
        default: () => ({}),
    },
    selectedUnit: {
        type: String,
        default: '',
    },
    selectedMonth: {
        type: String,
        default: '',
    },
    selectedSite: {
        type: String,
        default: '',
    },
});

const showImportModal = ref(false);
const showReviewModal = ref(false);
const showDebugPanel = ref(false);
const loading = ref(false);
const chartData = ref(null);

const currentMonth = new Date().toISOString().slice(0, 7);
const defaultSite = props.selectedSite || (props.siteIds.length === 1 ? props.siteIds[0] : '');
const allUnits = Object.values(props.unitsBySite).flat();
const defaultUnit = props.selectedUnit || (allUnits.length === 1 ? allUnits[0] : '');
const defaultMonth = props.selectedMonth || currentMonth;

const selectedSite = ref(defaultSite);
const selectedUnit = ref(defaultUnit);
const selectedMonth = ref(defaultMonth);

const filteredUnits = computed(() => {
    if (!selectedSite.value) return allUnits;
    return props.unitsBySite[selectedSite.value] ?? [];
});

watch(selectedSite, () => {
    if (selectedUnit.value && !filteredUnits.value.includes(selectedUnit.value)) {
        selectedUnit.value = filteredUnits.value.length === 1 ? filteredUnits.value[0] : '';
    }
});

const pendingReviewCount = computed(() => chartData.value?.availability?.pendingReviewCount ?? 0);
const hasScreeningData = computed(() => chartData.value?.hasScreeningData !== false);
const isDebug = computed(() => new URLSearchParams(window.location.search).get('debug') === '1');

function fmtDur(mins) {
    if (!mins) return '00:00:00';
    const totalSec = Math.round(mins * 60);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function shiftMonth(offset) {
    if (!selectedMonth.value) return;
    const [year, month] = selectedMonth.value.split('-').map(Number);
    const d = new Date(year, month - 1 + offset, 1);
    selectedMonth.value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

async function fetchChartData() {
    if (!selectedUnit.value || !selectedMonth.value) {
        chartData.value = null;
        return;
    }

    loading.value = true;

    try {
        const params = {
            unit_identifier: selectedUnit.value,
            month: selectedMonth.value,
        };
        if (isDebug.value) params.debug = 1;
        const url = buildUrlWithQuery(route('eds-operations.chart-data'), params);

        const response = await $http.get(url);
        chartData.value = response.data;
    } catch (error) {
        console.error('Error fetching chart data:', error);
    } finally {
        loading.value = false;
    }
}

watch(
    () => props.selectedSite,
    (val) => {
        if (val) {
            selectedSite.value = val;
        }
    },
);

watch(
    () => props.selectedUnit,
    (val) => {
        if (val) {
            selectedUnit.value = val;
        }
    },
);

watch(
    () => props.selectedMonth,
    (val) => {
        if (val) {
            selectedMonth.value = val;
        }
    },
);

watch([selectedUnit, selectedMonth], () => {
    fetchChartData();
});

onMounted(() => {
    if (selectedUnit.value && selectedMonth.value) {
        fetchChartData();
    }
});
</script>

<style scoped>
.smiths-dashboard__header {
    padding: 1rem 0;
}

.month-nav-btn {
    padding: 0 6px;
    border: none;
    background: none;
    cursor: pointer;
}

.btn-warning:hover {
    background-color: #d4960a !important;
    border-color: #c88d09 !important;
}

.month-nav-btn:hover {
    text-decoration: underline;
}
</style>
